export function formatRelativeAge(seconds: number | null | undefined): string | null {
  if (seconds == null || Number.isNaN(seconds)) return null;
  const value = Math.max(0, Math.floor(seconds));
  if (value < 60) return "gerade eben";
  if (value < 3600) {
    const minutes = Math.floor(value / 60);
    return `vor ${minutes} min`;
  }
  if (value < 86400) {
    const hours = Math.floor(value / 3600);
    return `vor ${hours} h`;
  }
  const days = Math.floor(value / 86400);
  return `vor ${days} d`;
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString("de-DE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export function substitutePlaceholders(
  template: string,
  values: Record<string, string>,
  opts?: { highlightUnset?: boolean; escape?: boolean },
): string {
  const highlight = opts?.highlightUnset ?? true;
  const shouldEscape = opts?.escape ?? false;
  return template.replace(TOKEN_RE, (raw, name: string, offset: number, whole: string) => {
    let value = values[name];
    if (value) {
      // "bis {{end}}." with end = "4:30 p.m." must not end in "p.m..".
      if (value.endsWith(".") && whole[offset + raw.length] === ".") value = value.slice(0, -1);
      return shouldEscape ? escapeHtml(value) : value;
    }
    if (highlight) {
      return `<span class="unset-token">${escapeHtml(raw)}</span>`;
    }
    return raw;
  });
}

/** Placeholders replaced by their value; unknown or empty ones stay as {{name}}. */
export function fillPlaceholders(text: string, values: Record<string, string>): string {
  return text.replace(TOKEN_RE, (match, key: string) => values[key]?.trim() || match);
}

export function placeholderNames(text: string): string[] {
  return Array.from(text.matchAll(TOKEN_RE), (match) => match[1]);
}

export function unresolvedPlaceholderNames(
  text: string,
  values: Record<string, string>,
): string[] {
  return [...new Set(placeholderNames(text).filter((name) => !values[name]?.trim()))];
}

function collapsePlaintext(raw: string): string {
  const lines = raw.split(/\r?\n/).map((line) => line.split(/\s+/).filter(Boolean).join(" "));
  const out: string[] = [];
  let blank = false;
  for (const line of lines) {
    if (line === "") {
      if (!blank && out.length) {
        out.push("");
        blank = true;
      }
      continue;
    }
    blank = false;
    out.push(line);
  }
  return out.join("\n").trim();
}

function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCharCode(parseInt(code, 16)));
}

export function emptyHtml(html: string): boolean {
  const trimmed = html.trim();
  return (
    trimmed === "" ||
    trimmed === "<p></p>" ||
    trimmed === "<p><br></p>" ||
    trimmed === "<p><br/></p>" ||
    trimmed === "<p><br /></p>"
  );
}

export function htmlToText(html: string): string {
  const trimmed = html.trim();
  if (emptyHtml(trimmed)) {
    return "";
  }
  // Tag replacement, not innerText: detached DOMParser documents drop <p>/<br>
  // line breaks in Safari/Firefox (they fall back to concatenated textContent).
  let text = trimmed.replace(/\r\n/g, "\n");
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<hr\s*\/?>/gi, "\n\n----------\n\n");
  text = text.replace(/<li(\s[^>]*)?>/gi, "\n- ");
  text = text.replace(/<\/(p|div|h[1-6]|blockquote|pre|tr|table)>/gi, "\n");
  text = text.replace(/<(p|div|h[1-6]|blockquote|pre|table|tr)(\s[^>]*)?>/gi, "\n\n");
  text = text.replace(
    /<a\s[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_match, href: string, inner: string) => {
      const label = inner.replace(/<[^>]+>/g, "").trim();
      const url = href.trim();
      if (!label || label === url) return label || url;
      return `${label} (${url})`;
    },
  );
  text = text.replace(/<[^>]+>/g, "");
  return collapsePlaintext(decodeEntities(text));
}

const TOKEN_NODE_RE = /<span[^>]*data-placeholder="([A-Za-z0-9_]+)"[^>]*>[\s\S]*?<\/span>/g;

/** `{{key}}` → an atom span the editor renders as a chip. Storage stays `{{key}}`. */
export function templateToTokens(
  html: string,
  render: (name: string) => { label: string; filled: boolean },
): string {
  return html.replace(TOKEN_RE, (_raw, name: string) => {
    const { label, filled } = render(name);
    return `<span data-placeholder="${name}" data-filled="${filled ? "1" : "0"}">${escapeHtml(label)}</span>`;
  });
}

/** Inverse of `templateToTokens`; the backend only ever sees `{{key}}`. */
export function tokensToTemplate(html: string): string {
  return html.replace(TOKEN_NODE_RE, (_raw, name: string) => `{{${name}}}`);
}

const SOURCE_BLOCK = /^(p|h[1-6]|ul|ol|li|table|thead|tbody|tfoot|tr|td|th|blockquote|div|hr|br)$/;
const SOURCE_CONTAINER = /^(ul|ol|table|thead|tbody|tfoot|tr|blockquote|div)$/;
// Cells and list items stay on one line unless they hold blocks themselves.
const SOURCE_CELL = /^(li|td|th)$/;

/** One block element per line, lists and tables indented: for reading, not for sending. */
export function formatHtmlSource(html: string): string {
  const lines: string[] = [];
  const cells: string[] = [];
  let depth = 0;
  let line = "";
  let openCell = "";
  const flush = () => {
    if (line.trim()) lines.push("  ".repeat(depth) + line.trim());
    line = "";
    openCell = "";
  };
  for (const token of html.split(/(<[^>]+>)/)) {
    const tag = /^<(\/)?([a-zA-Z0-9]+)/.exec(token);
    const name = tag?.[2].toLowerCase() ?? "";
    if (!tag || !SOURCE_BLOCK.test(name)) {
      line += token;
      if (token.trim()) openCell = "";
      continue;
    }
    const closing = Boolean(tag[1]);
    if (name === "br") {
      line += token;
      flush();
    } else if (name === "hr") {
      flush();
      line = token;
      flush();
    } else if (!closing) {
      if (openCell) {
        // A block inside a cell: the cell gets its own lines.
        const cell = openCell;
        flush();
        cells.push(cell);
        depth += 1;
      } else {
        flush();
      }
      line = token;
      if (SOURCE_CONTAINER.test(name)) {
        flush();
        depth += 1;
      } else if (SOURCE_CELL.test(name)) {
        openCell = name;
      }
    } else if (SOURCE_CONTAINER.test(name) || (SOURCE_CELL.test(name) && cells.at(-1) === name && !line.trim())) {
      flush();
      if (!SOURCE_CONTAINER.test(name)) cells.pop();
      depth = Math.max(0, depth - 1);
      line = token;
      flush();
    } else {
      line += token;
      flush();
    }
  }
  flush();
  return lines.join("\n");
}
